import builder, { BuilderComponent } from '@builder.io/react';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useDisclosure,
  useMediaQuery
} from '@chakra-ui/react';
import { useForm } from '@mantine/form';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import BottomSheet from 'components/atoms/BottomSheet/BottomSheet';
import SeoContent from 'components/atoms/SeoContent/SeoContent';
import { useParams } from 'hooks/useParams';
import useSegment from 'hooks/useSegment';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalStore } from 'stores/global';
import {
  decodeFully,
  getDefaultFilters,
  isObjectEmpty,
  updateFacets
} from '../../../helpers/general';
import Breadcrumbs from '../../atoms/Breadcrumbs/Breadcrumbs';
import Container from '../../atoms/Container/Container';
import Icon from '../../atoms/Icon/Icon';
import ProductCardLoader from '../../atoms/Loaders/ProductCardLoader';
import NewProductCard from '../../atoms/NewProductCard/NewProductCard';
import ProcessSearch from '../../molecules/Search/ProcessSearch.js';
import Facet from '../Facet/Facet';
import SortBy from '../SortBy/SortBy';
import * as styles from './Catalog.module.css';
import { isMadPawsStore, getDate } from '../../../common/util';
import { useLocation } from '@reach/router';

builder.init(process.env.BUILDER_PUBLIC_API);
const whiteListedFacets = [
  'q',
  'product_categories.name',
  'brand_name',
  'pets',
  'healthCondition',
  'flavour',
  'foodType',
  'lifeStage',
  'calculated_price'
];

const Catalog = ({
  category,
  headerContent,
  footerContent,
  isSearch,
  customFilter,
  isBrand = false,
  seoContent,
  landingSearch = false,
  searchData
}) => {
  const { pathname } = useLocation();
  const baseFilter = isBrand
    ? customFilter ?? `brand_id=${category?.bigcommerce_id}`
    : `product_categories.entityId=${category?.bigcommerce_id}`;
  const { track } = useSegment();
  const [isPc] = useMediaQuery('(min-width: 800px)', {
    ssr: true,
    fallback: false
  });

  const { showHeader, setShowHeader, setTrackHeaderScroll } = useGlobalStore(state => state);
  const [loadMoreAction, setLoadMoreAction] = useState(false);

  const query = useParams('q');
  const searchText = landingSearch ? searchData?.searchQuery : query;
  const brand_name = useParams('brand_name');

  const [initialFacets, setInitialFacets] = React.useState([]);
  // const [facets, setFacets] = React.useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  //Note : Numeric filters won't work if wrapped in quotes
  const form = useForm({
    initialValues: {
      filters: getDefaultFilters(),
      sortBy: ''
    },
    transformValues: values => ({
      searchQuery: values.filters
        .filter(filter => filter.type !== 'q' && whiteListedFacets.includes(filter.type))
        .map(filter => {
          return filter.filters
            .map(
              inner => `${filter.type}:${filter.type === 'calculated_price' ? inner : `"${inner}"`}`
            )
            .join(filter.type === 'calculated_price' ? ' OR ' : ' OR ');
        })
        .join(' AND ')
    })
  });

  const fetchProducts = ({ pageParam = 0 }) => {
    const filters = {
      page: pageParam,
      sortBy: form.values.sortBy,
      filters: `${isSearch ? '' : baseFilter}${
        form.getTransformedValues().searchQuery
          ? `${isSearch ? '' : ' AND '} ${form.getTransformedValues().searchQuery}`
          : ''
      }`
    };
    let searchQuery = searchText ? decodeFully(searchText) : '';
    return ProcessSearch(searchQuery, 36, filters);
  };

  const { data: initialCatalog } = useQuery(
    ['initial_catalog', category?.bigcommerce_id, isSearch],
    async () =>
      ProcessSearch(searchText ? decodeFully(searchText) : '', 1, {
        page: 0,
        filters: `${isSearch ? '' : baseFilter}`
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    }
  );

  const {
    data,
    isFetching,
    isFetched,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage
  } = useInfiniteQuery({
    queryKey: [
      'catalog',
      category?.bigcommerce_id,
      isSearch,
      searchText,
      form.getTransformedValues().searchQuery,
      form.values.sortBy
    ],
    queryFn: fetchProducts,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // enabled: false,
    getNextPageParam: lastPage => {
      const productCollection = lastPage?.[0];
      let nextPage = undefined;
      if (
        productCollection &&
        productCollection.nbPages !== 0 &&
        productCollection?.page + 1 !== productCollection.nbPages
      ) {
        nextPage = productCollection?.page + 1;
      }
      return nextPage;
    }
  });

  const getStockStatus = searchList => {
    if (!searchList || !searchList?.hits || searchList?.hits.length === 0) {
      return 'No hits';
    }

    const isEverythingOutOfStock = searchList?.hits.every(item => item?.inventory_level === 0);
    if (isEverythingOutOfStock) {
      return 'out of stock';
    }

    const isSomeOutOfStock = searchList?.hits.some(item => item?.inventory_level === 0);
    if (isSomeOutOfStock) {
      return 'partial stock';
    }

    return 'fully stocked';
  };

  const sortSelectHandler = sort => {
    form.setValues({ sortBy: sort });
  };

  const filterSelectHandler = filter => {
    let filters = [...form.values.filters];
    let typeFilterIndex = filters.findIndex(f => f.type === filter.type);

    if (typeFilterIndex > -1) {
      // If filter type exists
      let typeFilter = filters[typeFilterIndex];
      let filterIndex = typeFilter.filters.indexOf(filter.name);

      if (filterIndex > -1) {
        // If filter name exists, remove it
        typeFilter.filters.splice(filterIndex, 1);
        // If no filter left in this type, remove the type filter
        if (typeFilter.filters.length === 0) {
          filters.splice(typeFilterIndex, 1);
        }
      } else {
        // If filter name doesn't exist, add it
        typeFilter.filters.push(filter.name);
      }
    } else {
      // If filter type doesn't exist, add it
      filters.push({
        type: filter.type,
        filters: [filter.name]
      });
    }

    form.setValues({ filters: filters });
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams();
      form.values.filters.forEach(filter => {
        const encodedFilters = filter.filters.map(value => encodeURIComponent(value)).join(',');
        searchParams.set(filter.type, encodedFilters);
      });
      // Only add sortBy to the searchParams if it's not empty
      if (form.values.sortBy) {
        searchParams.set('sortBy', form.values.sortBy);
      }

      let newRelativePathQuery = window.location.pathname;
      if (Array.from(searchParams).length) {
        newRelativePathQuery += '?' + searchParams.toString();
      }
      window.history.pushState({ path: newRelativePathQuery }, '', newRelativePathQuery);
    }
  }, [form.values.filters, form.values.sortBy]);

  useEffect(() => {
    const syncFiltersWithUrl = () => {
      if (typeof window !== 'undefined') {
        const searchParams = new URLSearchParams(window.location.search);
        let initialFilters = [];
        let initialSortBy = searchParams.get('sortBy'); // Will be undefined if not present in URL

        for (let param of searchParams) {
          // don't add sortBy as a filter
          if (param[0] !== 'sortBy') {
            // Decode the filter values from the URL
            const decodedFilters = param[1].split(',').map(value => decodeFully(value.trim()));

            initialFilters.push({
              type: param[0],
              filters: decodedFilters
            });
          }
        }

        let newState = { filters: initialFilters };

        // Only include sortBy in the state if it's not empty
        if (initialSortBy) {
          newState.sortBy = initialSortBy;
        }

        // Only set values if they differ from current state
        if (
          !_.isEqual(form.values.filters, initialFilters) ||
          (initialSortBy && form.values.sortBy !== initialSortBy)
        ) {
          form.setValues(newState);
        }
      }
    };

    // Run on initial render
    syncFiltersWithUrl();

    // Also run when history state changes
    window.addEventListener('popstate', syncFiltersWithUrl);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', syncFiltersWithUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.filters, form.values.sortBy]);

  useEffect(() => {
    if (isFetched && data?.pages?.length > 0) {
      let newFacets = data?.pages?.at(-1)?.[0]?.facets;
      if (data?.pages?.at(-1)?.[0]?.queryID) {
        localStorage.setItem('algoliaQueryId', data?.pages?.at(-1)?.[0]?.queryID);
      }
      if (initialCatalog?.[0]?.facets && !isObjectEmpty(initialCatalog?.[0]?.facets)) {
        const originalData = { ...initialCatalog?.[0]?.facets }; // Your original facets object
        const newData = { ...newFacets }; // Your new facets data
        const updatedFacets = updateFacets(originalData, newData);
        setInitialFacets(updatedFacets);
        if (initialCatalog?.[0]?.queryID) {
          localStorage.setItem('algoliaQueryId', initialCatalog?.[0]?.queryID);
        }
      }
      if (typeof window !== 'undefined' && !loadMoreAction) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 200);
      }
      setLoadMoreAction(false);
    }
  }, [data?.pages, isFetched, initialCatalog]);

  const lastPage = useMemo(() => {
    const searchList = data?.pages?.at(-1)?.[0];
    if (searchList) {
      track('Product List Viewed', {
        list_id: isSearch ? 'Search Results' : category.name,
        products: searchList?.hits?.map((node, index) => ({
          brand: node.brand_id,
          name: node.name,
          price: node.calculated_price,
          productId: node.entityId,
          sku: node.sku,
          url: node.custom_url.url
        }))
      });

      track('Viewed Search', {
        name: 'Viewed Search',
        product_type: searchList?.hits?.length > 0 ? searchList?.hits[0]?.product_type : '',
        first_item_oos: searchList?.hits[0]?.inventory_level === 0,
        search_items_oos_count: searchList?.hits.filter(item => item.inventory_level === 0)?.length,
        search_result_size: searchList?.hits?.length || 0,
        stock_status: getStockStatus(searchList),
        event_original_name: 'Viewed Search'
      });

      track('Loaded a Page', {
        stock_status: getStockStatus(searchList)
      });
    }
    return searchList;
  }, [data?.pages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isPc) {
      setShowHeader(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPc]);

  useEffect(() => {
    const pathSegments = pathname
      .split('/')
      .filter(segment => segment !== '')
      .join('_');
    if (pathSegments) {
      const scriptId = 'area10-script';
      const existingScript = document.getElementById(scriptId);
      // Remove existing script if it exists
      if (existingScript) {
        existingScript.parentNode.removeChild(existingScript);
      }
      const script = document.createElement('script');
      script.src = `//limitless.cmax.ai/petchemist.com.au/kronos/js/article_${pathSegments}.js?${getDate(
        false
      )}`;
      script.async = true;
      script.defer = true;
      script.id = scriptId;
      document.body.appendChild(script);
    }
    setTrackHeaderScroll(true);
    return () => {
      setTrackHeaderScroll(false); // Reset when the component is unmounted
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pageTitle = () => {
    let title = category?.name || '';
    if (isSearch) {
      title = searchText
        ? landingSearch
          ? searchData?.title
          : `Search results for "${decodeFully(searchText)}"`
        : brand_name;
    }
    return title;
  };
  // console.log('pageTitle', data);
  return (
    <>
      <div className={styles.root}>
        <Container size="large">
          <Breadcrumbs
            crumbs={[
              { link: '/', label: '' },
              { label: isSearch ? 'Search Results' : category.name }
            ]}
          />
          <Flex gap={10} className={styles.container} pos={'relative'} overflow="visible">
            {isPc && (
              <Facet
                fetching={isFetching}
                facets={initialFacets}
                exclude={category?.name}
                selectHandler={filterSelectHandler}
                activeFilters={form.values.filters}
              />
            )}
            <Box minW={0} flex={1} flexShrink={0}>
              <h1 className={styles.brandName}>{pageTitle()}</h1>
              {!isPc && (
                <Text className="text-charcoal-2" fontSize={14}>
                  {lastPage?.nbHits} results
                </Text>
              )}

              <div>
                <BuilderComponent content={headerContent} model="plp-banner" />
              </div>

              <Flex
                className={`${styles.filterContainer} ${showHeader ? styles.px0 : styles.px6} ${
                  showHeader ? styles.stickyBase : styles.fixedBase
                }`}
              >
                {isPc ? (
                  <Text className="text-charcoal-2" fontSize={14}>
                    {lastPage?.nbHits} results
                  </Text>
                ) : (
                  <Button
                    p="10px 27.5px"
                    variant={'outline'}
                    color={isMadPawsStore() ? 'var(--primary-default)' : '#14607A'}
                    borderColor={isMadPawsStore() ? 'var(--primary-default)' : '#14607A'}
                    borderWidth={'2px'}
                    rounded={'full'}
                    fontSize={14}
                    onClick={onOpen}
                    display={'flex'}
                    alignItems={'center'}
                    gap={3}
                    h={10}
                  >
                    <Icon symbol={'facet-adjust'} />
                    <span>Filters</span>
                  </Button>
                )}

                <SortBy selectHandler={sortSelectHandler} sortBy={form.values.sortBy} />
              </Flex>
              <HStack flexWrap={'wrap'}>
                {form.values.filters
                  ?.filter(fil => fil.type !== 'q' && whiteListedFacets.includes(fil.type))
                  ?.map((filter, index) => (
                    <React.Fragment key={index}>
                      {filter.filters.map((inner, index) => (
                        <Tag size={'md'} key={index}>
                          <TagLabel>{inner}</TagLabel>
                          <TagCloseButton
                            onClick={() =>
                              filterSelectHandler({
                                type: filter.type,
                                name: inner
                              })
                            }
                          />
                        </Tag>
                      ))}
                    </React.Fragment>
                  ))}
                {form.values.filters?.filter(
                  fil => fil.type !== 'q' && whiteListedFacets.includes(fil.type)
                )?.length > 0 && (
                  <Button
                    leftIcon={<Icon symbol={'rotate'} />}
                    variant={'ghost'}
                    color={isMadPawsStore() ? 'var(--primary-default)' : '#005A7A'}
                    rounded={'full'}
                    onClick={() => form.setFieldValue('filters', [])}
                    py={3}
                  >
                    CLEAR
                  </Button>
                )}
              </HStack>
              <SimpleGrid
                borderWidth={['0', '0', '.5px']}
                borderTopWidth={['.5px', '.5px']}
                borderColor="#E6ECF2"
                columns={[1, 1, 2, 3, 4]}
                my={2.5}
              >
                {isFetching && !isFetchingNextPage ? (
                  <>
                    {Array.from({ length: 12 }).map((_, index) => (
                      <ProductCardLoader key={index} />
                    ))}
                  </>
                ) : (
                  <>
                    {data?.pages?.map((page, index) => (
                      <React.Fragment key={index}>
                        {page?.[0]?.hits
                          .slice()
                          .sort((a, b) =>
                            a.inventory_level === 0 ? 1 : b.inventory_level === 0 ? -1 : 0
                          )
                          .map((node, index) => (
                            <NewProductCard
                              showVariants
                              removeRadius
                              key={index}
                              wFull
                              sku={node.sku}
                              variants={node.variants}
                              image={node.defaultImage?.url}
                              productId={node.entityId}
                              name={node.name}
                              landscape
                              price={node.price}
                              track={node.inventory_tracking !== 'none'}
                              salePrice={node.calculated_price}
                              inventoryLevel={node.inventory_level}
                              rating={node.reviews_rating_sum}
                              ratingCount={node.reviews_count}
                              url={node.custom_url.url}
                              customFields={node.customFields}
                              modifiers={node.modifiers}
                              autoshipDiscount={node.autoshipDiscount}
                            />
                          ))}
                      </React.Fragment>
                    ))}
                  </>
                )}
              </SimpleGrid>
              {query && lastPage?.nbHits === 0 && (
                <Text className="text-charcoal-2" fontSize={15} textAlign={'center'}>
                  No results found for "{query}"
                </Text>
              )}

              <Stack p={4} spacing={4} my={4}>
                {lastPage?.nbHits > 0 && (
                  <Text className="text-charcoal-2" fontSize={15} textAlign={'center'}>
                    Results&nbsp;
                    {data?.pages?.reduce(
                      (total, currentPage) => total + currentPage?.[0]?.hits?.length,
                      0
                    )}{' '}
                    of {lastPage?.nbHits}
                  </Text>
                )}

                {hasNextPage && (
                  <Center>
                    <Button
                      fontSize={15}
                      variant={'outline'}
                      color={isMadPawsStore() ? 'var(--primary-default)' : '#14607A'}
                      borderColor={isMadPawsStore() ? 'var(--primary-default)' : '#14607A'}
                      rounded={'full'}
                      isLoading={isFetchingNextPage}
                      onClick={() => {
                        setLoadMoreAction(true);
                        fetchNextPage();
                      }}
                      _hover={{ bg: '#F4F8F8', color: '#1D6464', borderColor: '#1D6464' }}
                    >
                      LOAD MORE
                    </Button>
                  </Center>
                )}
              </Stack>

              <BuilderComponent content={footerContent} model="plp-bottom-section" />
            </Box>
          </Flex>
          {seoContent && <SeoContent {...seoContent} />}
        </Container>
        {!isPc && (
          <BottomSheet isOpen={isOpen} onClose={onClose}>
            <Flex justify={'space-between'} mt={5} align={'center'}>
              <Text fontSize={24}>Filters</Text>
              <IconButton h={8} w={8} variant={'ghost'} color={'#202020'} onClick={onClose}>
                <Icon symbol={'close'} />
              </IconButton>
            </Flex>

            <Box overflowY={'auto'} maxH={'calc(80vh - 120px)'}>
              <Box my={6} h={'0'} border={'0.5px solid #D5DFE9'}></Box>
              <Facet
                forceOpen
                exclude={category?.name}
                facets={initialFacets}
                selectHandler={filterSelectHandler}
                activeFilters={form.values.filters}
              />
            </Box>

            <Flex align={'center'} justify={'center'} mt={5} gap={8}>
              <Button
                leftIcon={<Icon symbol={'rotate'} />}
                variant={'ghost'}
                color={isMadPawsStore() ? 'var(--primary-default)' : '#005A7A'}
                rounded={'full'}
                onClick={() => form.setFieldValue('filters', [])}
                px={8}
                py={3}
              >
                CLEAR
              </Button>
              <Button
                colorScheme="teal"
                color={'white'}
                bg={'#005A7A'}
                px={8}
                py={3}
                rounded={'full'}
                onClick={onClose}
              >
                APPLY
              </Button>
            </Flex>
          </BottomSheet>
        )}
      </div>
      <div id="a10_kronos"></div>
    </>
  );
};

export default Catalog;
